/**
 * Ein Upload-Feld der Immobilien-Erfassung — an allen Stellen dasselbe.
 *
 * Ersetzt den früheren `UploadPlaceholder`: dessen Optik und Text leben hier
 * als Modus `disabled` weiter (öffentliche Wege ohne Session), die beiden
 * anderen Modi laden wirklich hoch. Welcher Modus gilt, entscheidet der
 * `UploadBufferProvider` — siehe `uploadBuffer.tsx`.
 *
 * Bedienung: Klick **oder** Drag-and-drop. Bildvorschau über
 * `URL.createObjectURL` (im Cleanup wieder freigegeben); alles andere — und
 * jedes Bild, das der Browser nicht rendert (HEIC!) — fällt auf einen
 * Datei-Chip zurück.
 *
 * Die Grenzen (Größe, gesperrte Endungen, Anzahl) prüft `uploadBuffer`
 * **in JS**, nicht nur über das `accept`-Attribut: Drag-and-drop und die
 * Auswahl „Alle Dateien" umgehen `accept` vollständig.
 */
import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { isPreviewImage } from "../documents/documentKind";
import { immoText, type Ansprache } from "./texte";
import {
  formatBytes,
  MAX_FILES_PER_SLOT,
  MAX_UPLOAD_BYTES,
  useUploadBuffer,
  type PendingUpload,
  type UploadScope,
} from "./uploadBuffer";
import type { DocumentDto } from "../../services/documentService";

const useStyles = makeStyles({
  // ── Modus „disabled" — 1:1 die frühere Platzhalter-Optik ──
  hinweis: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    backgroundColor: BRAND.bg,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    color: BRAND.muted,
    ...shorthands.border("1px", "dashed", "#d4cfd9"),
    ...shorthands.borderRadius("6px"),
  },
  icon: {
    flexShrink: 0,
    color: BRAND.muted,
  },

  // ── aktive Modi ──
  wrap: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  dropzone: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    width: "100%",
    boxSizing: "border-box",
    textAlign: "left",
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.bg,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "dashed", "#d4cfd9"),
    ...shorthands.borderRadius("6px"),
    transitionProperty: "background-color, border-color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: "#f5eef6",
      ...shorthands.borderColor(BRAND.purple),
    },
    ":focus-visible": {
      ...shorthands.borderColor(BRAND.purple),
      outlineWidth: 0,
    },
    ":disabled": {
      cursor: "progress",
      opacity: 0.6,
    },
  },
  dropzoneAktiv: {
    backgroundColor: "#f5eef6",
    ...shorthands.borderColor(BRAND.purple),
  },
  dropzoneText: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    flexGrow: 1,
    minWidth: 0,
  },
  dropzoneTitel: {
    fontWeight: 600,
    color: BRAND.ink,
  },
  dropzoneHint: {
    fontSize: "0.75rem",
    color: BRAND.muted,
  },
  versteckt: {
    display: "none",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(104px, 1fr))",
    gap: "8px",
  },
  kachel: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "4px",
    minHeight: "92px",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "6px",
    paddingRight: "6px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", "#e3dee8"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.overflow("hidden"),
  },
  kachelFehler: {
    ...shorthands.borderColor("#b91c1c"),
  },
  bild: {
    width: "100%",
    height: "56px",
    objectFit: "cover",
    display: "block",
    ...shorthands.borderRadius("4px"),
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    color: BRAND.purple,
    backgroundColor: BRAND.bg,
    ...shorthands.borderRadius("6px"),
  },
  name: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.68rem",
    lineHeight: 1.25,
    color: BRAND.ink,
    textAlign: "center",
    wordBreak: "break-word",
    width: "100%",
  },
  meta: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.65rem",
    color: BRAND.muted,
  },
  entfernen: {
    position: "absolute",
    top: "4px",
    right: "4px",
    width: "20px",
    height: "20px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "rgba(0,0,0,0.55)",
    backgroundImage: "none",
    color: "#ffffff",
    fontSize: "0.8rem",
    lineHeight: 1,
    fontWeight: 700,
    ...shorthands.border("0"),
    ...shorthands.borderRadius("999px"),
    ":hover": {
      backgroundColor: "rgba(0,0,0,0.75)",
    },
  },
  fehlerListe: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: "#b91c1c",
  },
  statusZeile: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.75rem",
    color: BRAND.muted,
  },
});

const ClipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d="M11 4l-5 5a2 2 0 0 0 3 3l6-6a4 4 0 0 0-6-6L3 6a6 6 0 0 0 8 8l3-3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const DateiIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M11.5 2.5H6a1.5 1.5 0 0 0-1.5 1.5v12A1.5 1.5 0 0 0 6 17.5h8a1.5 1.5 0 0 0 1.5-1.5V6.5l-4-4Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
      fill="none"
    />
    <path d="M11.5 2.5v4h4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Vorschau einer noch nicht hochgeladenen Datei. HEIC meldet zwar
 * `image/heic`, rendert aber in keinem Browser — deshalb der `onError`-Rückfall
 * auf den Chip statt einer kaputten Bildfläche.
 */
const PendingKachel = ({
  eintrag,
  onRemove,
  styles,
}: {
  eintrag: PendingUpload;
  onRemove: () => void;
  styles: ReturnType<typeof useStyles>;
}) => {
  const zeigtBild = isPreviewImage(eintrag.file.name, eintrag.file.type);
  const [bildKaputt, setBildKaputt] = React.useState(false);
  const [url, setUrl] = React.useState("");
  // Bewusst im Effekt, nicht im `useMemo`: `URL.createObjectURL` ist ein
  // Seiteneffekt. Im StrictMode rendert React doppelt — die im Render erzeugte
  // erste URL würde nie freigegeben (das Cleanup kennt nur die zuletzt
  // gemerkte) und leckte bei vielen Fotos spürbar Speicher.
  React.useEffect(() => {
    if (!zeigtBild) {
      setUrl("");
      return;
    }
    const next = URL.createObjectURL(eintrag.file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [eintrag.file, zeigtBild]);

  return (
    <div className={mergeClasses(styles.kachel, eintrag.status === "error" && styles.kachelFehler)}>
      {zeigtBild && !bildKaputt && url ? (
        <img
          className={styles.bild}
          src={url}
          alt={eintrag.file.name}
          onError={() => setBildKaputt(true)}
        />
      ) : (
        <span className={styles.chip}>
          <DateiIcon />
        </span>
      )}
      <span className={styles.name}>{eintrag.file.name}</span>
      <span className={styles.meta}>
        {eintrag.status === "uploading"
          ? "wird hochgeladen…"
          : eintrag.status === "error"
            ? "fehlgeschlagen"
            : formatBytes(eintrag.file.size)}
      </span>
      <button
        type="button"
        className={styles.entfernen}
        onClick={onRemove}
        aria-label={`${eintrag.file.name} entfernen`}
        title="Entfernen"
      >
        ×
      </button>
    </div>
  );
};

/**
 * Bereits am Risikoobjekt hängendes Dokument (Modus `immediate`). Die Vorschau
 * kostet den **vollen** Download — es gibt keinen Thumbnail-Endpunkt —, deshalb
 * wird sie erst geladen, wenn die Kachel im Blickfeld ist.
 */
const DokumentKachel = ({
  dokument,
  onRemove,
  styles,
}: {
  dokument: DocumentDto;
  onRemove: () => void;
  styles: ReturnType<typeof useStyles>;
}) => {
  const zeigtBild = isPreviewImage(dokument.fileName, dokument.mimeType);
  return (
    <div className={styles.kachel}>
      <span className={styles.chip}>
        <DateiIcon />
      </span>
      <span className={styles.name}>{dokument.fileName}</span>
      <span className={styles.meta}>
        {zeigtBild ? "Bild" : "Datei"}
        {dokument.fileSize != null ? ` · ${formatBytes(dokument.fileSize)}` : ""}
      </span>
      <button
        type="button"
        className={styles.entfernen}
        onClick={onRemove}
        aria-label={`${dokument.fileName} löschen`}
        title="Löschen"
      >
        ×
      </button>
    </div>
  );
};

export type UploadFieldProps = {
  /** `"global"` (Objektebene) oder die Gebäude-UUID. */
  scope: UploadScope;
  /** Slot-Schlüssel ohne Gebäude-Scope, z. B. `hv-vertrag`. */
  slotKey: string;
  label: string;
  accept?: string;
  multiple?: boolean;
  /**
   * Nur für den Modus `disabled` relevant: im Kundenformular wird ein
   * Upload-Link angekündigt, intern trifft das nicht zu (E11).
   */
  ansprache?: Ansprache;
};

/** Bilder und die üblichen Nachweisformate — bewusst großzügig. */
const DEFAULT_ACCEPT =
  "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.odt,.ods,.txt,.rtf,.heic,.heif";

const UploadField = ({
  scope,
  slotKey,
  label,
  accept = DEFAULT_ACCEPT,
  multiple = true,
  ansprache = "kunde",
}: UploadFieldProps) => {
  const styles = useStyles();
  const buffer = useUploadBuffer();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [dragAktiv, setDragAktiv] = React.useState(false);
  const [meldungen, setMeldungen] = React.useState<string[]>([]);

  const aktiv = buffer.mode.mode !== "disabled";
  const vorgemerkt = buffer.filesFor(scope, slotKey);
  const vorhanden = buffer.docsFor(scope, slotKey);

  /**
   * Im Modus `immediate` läuft der Upload sofort und über EINE Warteschlange.
   * Während sie abgearbeitet wird, bleibt das Feld gesperrt — sonst reihte ein
   * hektischer zweiter Drop weitere Dateien in einen Lauf ein, dessen
   * Fortschritt gerade nicht sichtbar ist.
   */
  const gesperrt = (buffer.mode.mode === "immediate" || buffer.mode.mode === "token") && buffer.busy;

  const uebernehmen = (files: FileList | File[] | null) => {
    if (gesperrt) return;
    const liste = files ? Array.from(files) : [];
    if (liste.length === 0) return;
    setMeldungen(buffer.add(scope, slotKey, label, liste));
  };

  if (!aktiv) {
    // Exakt der frühere Platzhalter — die öffentlichen Wege bleiben unverändert.
    return (
      <div className={styles.hinweis}>
        <span className={styles.icon}>
          <ClipIcon />
        </span>
        <span>
          {label} — {immoText("uploadHinweis", ansprache)}
        </span>
      </div>
    );
  }

  const anzahl = vorgemerkt.length + vorhanden.length;
  const gepuffert = buffer.mode.mode === "buffer";

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={mergeClasses(styles.dropzone, dragAktiv && styles.dropzoneAktiv)}
        disabled={gesperrt}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!dragAktiv) setDragAktiv(true);
        }}
        onDragLeave={() => setDragAktiv(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragAktiv(false);
          uebernehmen(e.dataTransfer?.files ?? null);
        }}
        aria-label={`${label} hochladen`}
      >
        <span className={styles.icon}>
          <ClipIcon />
        </span>
        <span className={styles.dropzoneText}>
          <span className={styles.dropzoneTitel}>{label}</span>
          <span className={styles.dropzoneHint}>
            {gesperrt
              ? "Datei wird hochgeladen…"
              : anzahl >= MAX_FILES_PER_SLOT
                ? `Maximal ${MAX_FILES_PER_SLOT} Dateien erreicht.`
                : `Datei auswählen oder hierher ziehen · max. ${formatBytes(MAX_UPLOAD_BYTES)} je Datei`}
          </span>
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        className={styles.versteckt}
        accept={accept}
        multiple={multiple}
        onChange={(e) => {
          uebernehmen(e.target.files);
          // Zurücksetzen, damit dieselbe Datei erneut gewählt werden kann.
          e.target.value = "";
        }}
      />

      {meldungen.length > 0 && (
        <div className={styles.fehlerListe} role="alert">
          {meldungen.map((m, i) => (
            <span key={i}>{m}</span>
          ))}
        </div>
      )}

      {anzahl > 0 && (
        <div className={styles.grid}>
          {vorhanden.map((d) => (
            <DokumentKachel
              key={`doc-${d.id}`}
              dokument={d}
              onRemove={() => buffer.removeDocument(d.id)}
              styles={styles}
            />
          ))}
          {vorgemerkt.map((p) => (
            <PendingKachel
              key={p.id}
              eintrag={p}
              onRemove={() => buffer.remove(p.id)}
              styles={styles}
            />
          ))}
        </div>
      )}

      {gepuffert && vorgemerkt.length > 0 && (
        <span className={styles.statusZeile}>
          {vorgemerkt.length === 1
            ? "1 Datei vorgemerkt — sie wird nach dem Speichern hochgeladen."
            : `${vorgemerkt.length} Dateien vorgemerkt — sie werden nach dem Speichern hochgeladen.`}
        </span>
      )}
    </div>
  );
};

export default UploadField;
